import { useEffect, useState, useCallback } from "react";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import ApModalDialog from "@/components/ApModalDialog";
import TanstackTable from "@/components/TanstackTable";
import { usePopupDisplay } from "@/hooks/usePopupDisplay";
import { UserListApi } from "@/services/UserList.api";
import { UserList } from "@/shared/models/UserList";
import { Button, Row, Col, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Pages } from "@/shared/Constants";
import { isAdmin as hasAdminRole } from "@/shared/utils/roles";
import UserViewEditForm from "./UserViewEditForm";
import { UserRoleDisplayNames, UserRoles } from "@/shared/Enums";
import { DateTimeHelper } from "@/shared/utils/DateTime.helper";
import { dateRangeFilterFn } from "@/components/TanstackTable/Filter";
import CardContainer from "@/components/CardContainer";
import { useCommonData } from "@/hooks/useCommonData";

const UserView = () => {
  const { popupVisible, showPopUp, hidePopup } = usePopupDisplay();
  const navigate = useNavigate();

  // Get current user's role from Redux
  const { userRoles } = useSelector((state: RootState) => state.auth);
  const isAdmin = hasAdminRole(userRoles);

  const [data, setData] = useState([] as UserList[]);
  const [selectedUser, setSelectedUser] = useState({} as UserList);
  // Default: exclude reader-only users per requirement
  const [excludeReaderOnly, setExcludeReaderOnly] = useState(true);
  const { loadCommonData } = useCommonData();

  const columnHelper = createColumnHelper<UserList>();

  const columns = [
    columnHelper.accessor("apexId", { header: "ApexId", cell: (info) => info.getValue() }),
    columnHelper.accessor("userName", {
      header: "User Name",
      cell: (info: CellContext<UserList, string>) => (
        <Button
          onClick={() => {
            const user = info.row.original;
            navigate(`/${Pages.clientProfile}?apexId=${user.apexId}`, {
              state: { from: Pages.userManagement, user },
              replace: false,
            });
          }}
          className="p-0 text-primary text-decoration-underline"
          variant="link"
          style={{ textAlign: "left" }}
        >
          {info.getValue()}
        </Button>
      ),
    }),
    // columnHelper.accessor("email", { header: "Email", cell: (info) => info.getValue() }),
    // columnHelper.accessor("team", { header: "Team", cell: (info) => info.getValue() }),
    columnHelper.accessor("userRole", {
      header: "User Role",
      cell: (info) => {
        const roles = info.getValue();
        if (!Array.isArray(roles)) return "";

        // Map each role to its display name
        return roles
          .map((role) => {
            const roleKey = Object.keys(UserRoles).find((key) => UserRoles[key as keyof typeof UserRoles] === role) as
              | keyof typeof UserRoles
              | undefined;

            return roleKey ? UserRoleDisplayNames[UserRoles[roleKey]] : role;
          })
          .join(", ");
      },
    }),
    columnHelper.accessor("domain", { header: "Domain", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("lastModifiedAt", {
      header: "Last Modified At",
      cell: (info) => DateTimeHelper.ToLocalString(info.getValue()!),
      filterFn: dateRangeFilterFn,
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) =>
        isAdmin ? (
          <Button
            onClick={() => {
              setSelectedUser(info.row.original);
              showPopUp();
            }}
            variant="outline-primary"
            size="sm"
          >
            <i className="fas fa-edit me-1"></i>
            Edit
          </Button>
        ) : (
          <span className="text-muted small">
            <i className="fas fa-lock me-1"></i>
            No Access
          </span>
        ),
    }),
  ];

  const loading = useCallback(async () => {
    const userListData = await UserListApi.getAllData(excludeReaderOnly);
    setData(userListData);
  }, [excludeReaderOnly]);

  useEffect(() => {
    loading();
  }, [loading]);

  useEffect(() => {
    loadCommonData();
  }, [loadCommonData]);

  const handleExcludeToggleChange = (checked: boolean) => {
    // When toggle ON (checked) we SHOW reader-only users => excludeReadOnly = false
    setExcludeReaderOnly(!checked);
  };

  const handleUserListItemSubmit = async (user: UserList) => {
    if (await UserListApi.updateUserList(user)) {
      hidePopup();
      await loading();
    }
  };

  return (
    <>
      <CardContainer
        title="User Management"
        subTitle="Manage user roles and profiles"
        itemCount={data.length}
        itemText={"Users " + (excludeReaderOnly ? "(Readonly Hidden)" : "(Including Readonly)")}
      >
        <div>
          {/* Filter Controls */}
          <div className="bg-light border-bottom p-3">
            <Row className="align-items-center">
              <Col>
                <Form.Check
                  type="switch"
                  id="exclude-reader-toggle"
                  label="Show Readonly Users"
                  checked={!excludeReaderOnly}
                  onChange={(e) => handleExcludeToggleChange(e.target.checked)}
                  className="fw-semibold"
                />
              </Col>
              <Col xs="auto">
                <Button variant="outline-primary" size="sm" onClick={loading}>
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </Button>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <div className="p-3">
            <TanstackTable data={data} columns={columns}></TanstackTable>
          </div>
        </div>
      </CardContainer>

      <ApModalDialog
        props={{
          show: popupVisible,
          onHide: hidePopup,
          backdrop: "static",
          keyboard: false,
          autoFocus: true,
        }}
        header={true}
        headerText="User Role Edit"
        footer={false}
        size="lg"
        dialogClassName="modal-fit"
      >
        <UserViewEditForm userData={selectedUser} onSubmit={handleUserListItemSubmit} onCancel={hidePopup} />
      </ApModalDialog>
    </>
  );
};

export default UserView;
